import { ChatCustomBlockTypes, type BlockButtons, type ChatFastAction } from "@do/shared-types";
import { cn } from "@do/ui/lib/utils";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMotionEnabled } from "../../../../hooks/use-motion-enabled";
import { getButtonDelaysMs } from "../../../../lib/button-delays";
import {
  BUTTON_REVEAL_OFFSET_Y,
  BUTTON_REVEAL_SCALE_FROM,
  BUTTON_REVEAL_TIMING,
  getButtonRevealDelayS,
  getRevealProps,
} from "../../../../lib/motion-presets";
import { useChat } from "../../../../providers/chat-provider";
import { MessageAppStoreButton } from "./app-store-button";
import { MessageButton, type MessageButtonProps } from "./button";
import { MessagePlayStoreButton } from "./play-store-button";

const safeParseTemplate = (template: unknown | undefined): MessageButtonProps["template"] => {
  if (!template || typeof template !== "string" || template.trim() === "") {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(template);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as MessageButtonProps["template"];
    }
    return {};
  } catch {
    return {};
  }
};

export const getButtonType = (
  button: Pick<ChatFastAction, "appId" | "code" | "payload" | "tel" | "url">
) => {
  if (button.appId) {
    return "store";
  } else if (button.url) {
    return "url";
  } else if (button.tel) {
    return "tel";
  } else if (button.code) {
    return "code";
  }
  return "payload";
};

const getButtonClassName = (
  button: Pick<ChatFastAction, "appId" | "code" | "payload" | "tel" | "url">
) => {
  if (button.appId) {
    return "chat-message-button-store";
  } else if (button.url) {
    return "chat-message-button-url";
  } else if (button.tel) {
    return "chat-message-button-tel";
  } else if (button.code) {
    return "chat-message-button-code";
  }
  return "chat-message-button-redirection";
};

type MessageButtonsProps = {
  block: BlockButtons;
  canBackToPreviousStep: boolean;
  isGrid: boolean;
  /**
   * When the parent already waited before mounting this component (e.g. footer
   * registration), continue the reveal from that elapsed time instead of 0.
   */
  initialElapsedMs?: number;
  /**
   * The message's own entrance delay: the cascade starts when the message does, rather than
   * playing out under a message still invisible.
   */
  revealDelayS?: number;
};

export const MessageButtons = ({
  block,
  canBackToPreviousStep,
  isGrid,
  initialElapsedMs = 0,
  revealDelayS = 0,
}: MessageButtonsProps) => {
  const { disabled, footerContent, selectedLocale, defaultLocale } = useChat();
  const t = useTranslations("do-chat-v2");
  const motionEnabled = useMotionEnabled();

  const buttons = useMemo(() => {
    if (canBackToPreviousStep) {
      return [
        {
          id: "back-button",
          type: ChatCustomBlockTypes.buttons,
          title:
            selectedLocale?.backButtonLabel ||
            defaultLocale?.backButtonLabel ||
            t("chat.messages.actions.back-to-previous-step"),
        },
        ...block.buttons,
      ];
    }
    return block.buttons;
  }, [block.buttons, canBackToPreviousStep, t, selectedLocale, defaultLocale]);

  // A block level delay applies to every button it contains, so it acts as a floor.
  const delaysMs = useMemo(
    () => getButtonDelaysMs(buttons, block.delay ?? 0),
    [buttons, block.delay]
  );
  const delaysKey = delaysMs.join("|");

  const [elapsedMs, setElapsedMs] = useState(initialElapsedMs);

  useEffect(() => {
    setElapsedMs(initialElapsedMs);

    const timers = [...new Set(delaysMs)]
      .filter((delayMs) => delayMs > initialElapsedMs)
      .map((delayMs) =>
        window.setTimeout(
          () => setElapsedMs((current) => Math.max(current, delayMs)),
          delayMs - initialElapsedMs
        )
      );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // `delaysMs` is a new array on every render; `delaysKey` captures its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delaysKey, initialElapsedMs]);

  const visibleButtons = useMemo(
    () =>
      buttons
        .map((button, index) => ({ button, index }))
        .filter(({ index }) => (delaysMs[index] ?? 0) <= elapsedMs),
    [buttons, delaysMs, elapsedMs]
  );

  const revealedCountRef = useRef(0);

  useEffect(() => {
    revealedCountRef.current = visibleButtons.length;
  }, [visibleButtons.length]);

  const alreadyRevealedCount = revealedCountRef.current;

  if (visibleButtons.length === 0) {
    return null;
  }

  return (
    <div
      id={`message-${block.id}-buttons-container`}
      className={cn(
        "gap-(--message-buttons-gap)",
        isGrid ? "grid grid-cols-1 @xl/messages:grid-cols-2 group/grid" : "flex flex-wrap"
      )}
      role="group"
      aria-label={t("chat.messages.accessibility.message-buttons-aria-label")}
    >
      {visibleButtons.map(({ button, index }, position) => {
        const isStore = !!button.appId;
        const metadataStr = JSON.stringify(Object.assign({}, ...(button.metadata || [])));
        const isBackButton = button.id === "back-button";
        const template = safeParseTemplate(button.template);
        const buttonProps: MessageButtonProps = {
          button,
          buttonType: getButtonType(button),
          className: cn(
            "chat-message-button",
            getButtonClassName(button),
            // `w-full` because the button is no longer the grid item — the animated
            // wrapper is. A grid item with `w-auto` stretches to its column on its own,
            // but inside that wrapper the button is a flex item, and a flex item sizes to
            // its own content unless it is told otherwise.
            isGrid && "justify-start h-full self-stretch w-full",
            button.html_classes
          ),
          "data-button-metadata": metadataStr,
          disabled: disabled || !!footerContent,
          isBackButton,
          template,
          id: block.html_id ?? `${block.id}-button-${index}`,
          "data-test": `${block.id}-button-${index}`,
        };

        // A store block shows one button per platform, each its own item so that the
        // container's gap separates them and, in a grid, each takes a cell. Off its
        // platforms it renders nothing, and no empty item takes up a gap.
        const buttonNodes = isStore
          ? [
              window.isIos && <MessageAppStoreButton {...buttonProps} />,
              window.isAndroid && <MessagePlayStoreButton {...buttonProps} />,
            ].filter(Boolean)
          : [<MessageButton {...buttonProps} />];

        return buttonNodes.map((node, platform) => (
          // The wrapper takes over the flex/grid item role, so it has to pass on the
          // sizing the button used to get straight from the container: cap the width so
          // long labels keep wrapping, and stay a flex box so the button still stretches
          // to the height of its row instead of shrinking to its own text.
          <motion.div
            key={`${button.id}-${index}-${platform}`}
            className={cn("flex min-w-0 max-w-full", isGrid && "h-full")}
            {...getRevealProps({
              motionEnabled,
              timing: BUTTON_REVEAL_TIMING,
              offsetY: BUTTON_REVEAL_OFFSET_Y,
              scaleFrom: BUTTON_REVEAL_SCALE_FROM,
              delayS: revealDelayS + getButtonRevealDelayS(position, alreadyRevealedCount),
              // The whole set leaves at once: whatever replaces it waits on this exit,
              // so cascading out would just delay the next buttons.
              withExit: true,
            })}
          >
            {node}
          </motion.div>
        ));
      })}
    </div>
  );
};
